import {twMerge} from '../../../common/twMerge';
import {ButtonV2Size, ButtonV2Variant} from './ButtonV2.enum';
import {ButtonV2Props} from './ButtonV2Props.interface';

/**
 * ButtonV2 컴포넌트
 *
 * 다양한 크기와 스타일을 지원하는 버튼 컴포넌트입니다.
 * Primary, Secondary, Line, Negative 등의 스타일 변형을 제공합니다.
 *
 * @component
 * @example
 * ```tsx
 * <ButtonV2
 *   size={ButtonV2Size.Large}
 *   variant={ButtonV2Variant.Primary}
 *   onClick={handleClick}
 * >
 *   버튼 텍스트
 * </ButtonV2>
 * ```
 *
 * @param {ButtonV2Size} [size] - 버튼 크기 (기본값: small)
 * @param {ButtonV2Variant} [variant] - 버튼의 스타일 변형 (Primary, Secondary, Line, Negative)
 * @param {boolean} [disabled] - 버튼의 비활성화 상태
 * @param {() => void} [onClick] - 버튼 클릭 시 실행될 함수
 * @param {boolean} [bottomSafeAreaPaddingNeeded] - 하단 safe area 패딩 필요 여부
 * @param {string} [className] - 추가 CSS 클래스명
 * @param children - 버튼 내부에 표시될 컨텐츠
 * @returns {JSX.Element} 렌더링된 ButtonV2 컴포넌트
 */
export function ButtonV2({
  size = ButtonV2Size.Small,
  variant = ButtonV2Variant.Primary,
  disabled,
  onClick,
  bottomSafeAreaPaddingNeeded,
  className,
  children,
  ...props
}: ButtonV2Props): JSX.Element {
  return (
    <button
      className={twMerge(
        'rounded-100pxr inline-flex justify-center items-center select-none',
        sizeToStyle[size],
        variantToStyle[variant],
        !disabled && 'active:opacity-30',
        disabled && 'cursor-default pointer-events-none',
        !onClick && 'cursor-default active:text-current',
        bottomSafeAreaPaddingNeeded && 'bottom-button-safe-area',
        className
      )}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

const sizeToStyle = {
  [ButtonV2Size.Small]: 'px-12pxr min-h-[28px] font-small2-bold',
  [ButtonV2Size.Large]: 'w-full px-20pxr min-h-[54px] font-medium1-bold',
};

const variantToStyle = {
  [ButtonV2Variant.Primary]:
    'bg-theme-etc-button-primary text-theme-solid-0 disabled:bg-theme-solid-20 disabled:text-theme-solid-10',
  [ButtonV2Variant.Secondary]:
    'bg-theme-etc-button-secondary text-theme-solid-100 disabled:bg-theme-solid-20 disabled:text-theme-solid-10',
  [ButtonV2Variant.Line]:
    'bg-transparent text-theme-solid-100 border border-solid border-theme-transparent-20 disabled:text-theme-solid-20',
  [ButtonV2Variant.Negative]:
    'bg-static-point-red text-white disabled:bg-theme-solid-20 disabled:text-theme-solid-10',
  [ButtonV2Variant.Static]: 'bg-static-point-yellow text-static-mono-black',
};
